/**
 * Runtime branding (Einstellungen → Branding, GET /api/v1/branding): derives the primary colour tokens and applies
 * them as a `<style id="blimo-branding">` block that overrides `packages/ui/styles/tokens.css` (and the portal
 * overrides) without touching the token structure. The default BLIMO orange keeps the hand-tuned tokens.
 *
 * Flash-free: the generated CSS is cached in localStorage and re-applied by an inline script in index.html before the
 * first paint (see BRANDING_BOOT_SCRIPT); the fresh value replaces it once the query has loaded.
 */

export const DEFAULT_PRIMARY = '#f38416';
export const BRANDING_STORAGE_KEY = 'blimo-branding-css';
export const BRANDING_STYLE_ID = 'blimo-branding';

/** Dark text on primary surfaces (≈ --primary-foreground oklch(0.2 0.03 50)); same constant as the backend (Contrast::DARK_TEXT). */
export const DARK_TEXT = '#1c1917';
/** Approximation of the dark-mode surfaces (cards/background) for the text shade check. */
const DARK_SURFACE = '#1b1e24';

/** @typedef {{ company_name?: string, short_name?: string|null, primary_color?: string, primary_foreground?: 'light'|'dark', welcome_text?: string|null, imprint_url?: string|null, privacy_url?: string|null, support_phone?: string|null, support_email?: string|null, logo_light_url?: string|null, logo_dark_url?: string|null, favicon_url?: string|null, customized?: boolean }} Branding */

/** @param {string} hex */
export function isHexColor(hex) {
  return typeof hex === 'string' && /^#[0-9a-fA-F]{6}$/.test(hex);
}

/** @param {string} hex @returns {[number, number, number]} */
function rgb(hex) {
  const n = Number.parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** @param {[number, number, number]} c */
function toHex(c) {
  return `#${c
    .map((v) =>
      Math.round(Math.min(255, Math.max(0, v)))
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;
}

/** WCAG relative luminance. @param {string} hex */
export function luminance(hex) {
  const [r, g, b] = rgb(hex).map((v) => {
    const c = v / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio (1–21). @param {string} a @param {string} b */
export function contrastRatio(a, b) {
  const [l1, l2] = [luminance(a), luminance(b)];
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
}

/** Linear sRGB mix (t = share of `target`). @param {string} hex @param {string} target @param {number} t */
function mix(hex, target, t) {
  const a = rgb(hex);
  const b = rgb(target);
  return toHex(/** @type {[number, number, number]} */ (a.map((v, i) => v + (b[i] - v) * t)));
}

/** Darkens/lightens `hex` towards `target` until it reaches `ratio` against `surface`. */
function shadeFor(hex, surface, target, ratio = 4.5) {
  for (let t = 0; t <= 1.0001; t += 0.04) {
    const candidate = mix(hex, target, t);
    if (contrastRatio(candidate, surface) >= ratio) return candidate;
  }
  return target;
}

/**
 * Token values for a primary colour.
 * @param {string} primary  hex `#rrggbb`
 * @returns {{ primary: string, foreground: string, textLight: string, textDark: string, contrast: { white: number, dark: number }, passesAA: boolean }}
 */
export function brandPalette(primary) {
  const p = isHexColor(primary) ? primary.toLowerCase() : DEFAULT_PRIMARY;
  const white = contrastRatio(p, '#ffffff');
  const dark = contrastRatio(p, DARK_TEXT);
  return {
    primary: p,
    foreground: white >= dark ? '#ffffff' : DARK_TEXT,
    textLight: shadeFor(p, '#ffffff', '#000000'),
    textDark: shadeFor(p, DARK_SURFACE, '#ffffff'),
    contrast: { white: Math.round(white * 100) / 100, dark: Math.round(dark * 100) / 100 },
    passesAA: Math.max(white, dark) >= 4.5,
  };
}

/**
 * CSS overriding the primary tokens (light + dark). Selectors use `html[data-brand]` so they win over `:root`, `.dark`
 * and `html[data-app='portal']`; the portal tints are `color-mix(var(--primary) …)` and follow automatically.
 * Returns '' for the default colour (hand-tuned tokens stay).
 * @param {string|undefined|null} primary
 */
export function brandingCss(primary) {
  if (!isHexColor(primary ?? '') || primary.toLowerCase() === DEFAULT_PRIMARY) return '';
  const p = brandPalette(primary);
  return [
    `html[data-brand]:root,html[data-brand]:root.dark{--primary:${p.primary};--ring:${p.primary};--primary-foreground:${p.foreground};}`,
    `html[data-brand]:root{--primary-text:${p.textLight};}`,
    `html[data-brand]:root.dark{--primary-text:${p.textDark};}`,
  ].join('\n');
}

/** Read (value undefined), write or remove (null) a localStorage entry. @param {string} key @param {string|null} [value] */
function storage(key, value) {
  try {
    if (value === undefined) return localStorage.getItem(key);
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // private mode / no storage – branding is applied without cache
  }
  return null;
}

/**
 * Applies colour tokens and favicon to the document (idempotent) and caches the CSS for the next start.
 * @param {Branding|null|undefined} branding
 */
export function applyBranding(branding) {
  if (typeof document === 'undefined' || !branding) return;
  const css = brandingCss(branding.primary_color);
  const root = document.documentElement;
  let style = /** @type {HTMLStyleElement|null} */ (document.getElementById(BRANDING_STYLE_ID));
  if (css) {
    if (!style) {
      style = document.createElement('style');
      style.id = BRANDING_STYLE_ID;
      document.head.appendChild(style);
    }
    if (style.textContent !== css) style.textContent = css;
    root.setAttribute('data-brand', '');
    storage(BRANDING_STORAGE_KEY, css);
  } else {
    style?.remove();
    root.removeAttribute('data-brand');
    storage(BRANDING_STORAGE_KEY, null);
  }
  applyFavicon(branding.favicon_url ?? null);
}

/** Custom favicon (or the bundled ones). @param {string|null} url */
function applyFavicon(url) {
  const links = /** @type {HTMLLinkElement[]} */ ([...document.querySelectorAll('link[rel="icon"]')]);
  for (const link of links) {
    if (link.dataset.default === undefined) {
      link.dataset.default = link.getAttribute('href') ?? '';
      link.dataset.defaultType = link.getAttribute('type') ?? '';
    }
    const next = url ?? link.dataset.default;
    if (link.getAttribute('href') !== next) link.setAttribute('href', next);
    if (url) link.removeAttribute('type');
    else if (link.dataset.defaultType) link.setAttribute('type', link.dataset.defaultType);
  }
}

/** Removes runtime branding (tests, logout of a preview). */
export function resetBranding() {
  if (typeof document === 'undefined') return;
  document.getElementById(BRANDING_STYLE_ID)?.remove();
  document.documentElement.removeAttribute('data-brand');
}
